// Pure helpers for the admin Daily Schedule page (pages/admin/AdminDailySchedule.jsx).
// Dates are handled as local "YYYY-MM-DD" keys, the same format the schedule
// API takes for `date` / `startDate` / `endDate` and returns as group dates.

export const PERIOD_WEEKS = 2;
export const DAYS_PER_WEEK = 7;
export const VISIT_TITLE = "Centre Visit";

const COACH_ROLES = ["coach", "sports-coach", "music-coach"];

// Pin colours for the centres, assigned in list order (mockup palette).
export const CENTRE_COLORS = [
  "#2563eb",
  "#ec4899",
  "#3b82f6",
  "#1e3a8a",
  "#ef4444",
  "#f472b6",
  "#166534",
  "#eab308",
  "#14b8a6",
  "#9333ea",
];

// Fixed names rather than toLocaleDateString: ICU versions disagree on
// abbreviations (en-GB gives "Sept" in newer ones; the mockup uses "Sep").
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTHS_SHORT = MONTHS_LONG.map((month) => month.slice(0, 3));

const pad = (value) => String(value).padStart(2, "0");

export const toDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const parseDateKey = (key) => {
  const [year, month, day] = String(key).split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

export const startOfWeekMonday = (date) => {
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
};

export const shiftDateKey = (key, days) => toDateKey(addDays(parseDateKey(key), days));

// Weeks shown on the page: consecutive 7-day blocks from the period start.
export const buildPeriodWeeks = (startKey, weeks = PERIOD_WEEKS) => {
  const start = parseDateKey(startKey);
  return Array.from({ length: weeks }, (_, weekIndex) => ({
    index: weekIndex,
    days: Array.from({ length: DAYS_PER_WEEK }, (_, dayIndex) => {
      const date = addDays(start, weekIndex * DAYS_PER_WEEK + dayIndex);
      return {
        key: toDateKey(date),
        date,
        weekday: WEEKDAYS_SHORT[date.getDay()],
        label: `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`,
      };
    }),
  }));
};

const monthLong = (date) => MONTHS_LONG[date.getMonth()];
const monthShort = (date) => MONTHS_SHORT[date.getMonth()];

// "1 – 14 September 2026"; short months when the range crosses a month so
// the header stays on one row: "28 Sep – 11 Oct 2026", "28 Dec 2026 – 10 Jan 2027".
export const formatPeriodLabel = (startKey, endKey) => {
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey);
  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.getDate()} ${monthShort(start)} ${start.getFullYear()} – ${end.getDate()} ${monthShort(end)} ${end.getFullYear()}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${start.getDate()} ${monthShort(start)} – ${end.getDate()} ${monthShort(end)} ${end.getFullYear()}`;
  }
  return `${start.getDate()} – ${end.getDate()} ${monthLong(end)} ${end.getFullYear()}`;
};

// "1 Sep – 7 Sep 2026" (week header).
export const formatWeekLabel = (startKey, endKey) => {
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey);
  const startYear = start.getFullYear() !== end.getFullYear() ? ` ${start.getFullYear()}` : "";
  return `${start.getDate()} ${monthShort(start)}${startYear} – ${end.getDate()} ${monthShort(end)} ${end.getFullYear()}`;
};

// "3 Sep 2026" (visit date field).
export const formatDateKeyLong = (key) => {
  if (!key) return "";
  const date = parseDateKey(key);
  return `${date.getDate()} ${monthShort(date)} ${date.getFullYear()}`;
};

// "Mon, 28 Sep 2026" (lesson plan date field).
export const formatDateKeyWithWeekday = (key) => {
  if (!key) return "";
  const date = parseDateKey(key);
  return `${WEEKDAYS_SHORT[date.getDay()]}, ${formatDateKeyLong(key)}`;
};

export const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

// The admin schedule API returns [{ date: "YYYY-MM-DD", schedules: [...] }].
export const flattenScheduleGroups = (groups) =>
  (Array.isArray(groups) ? groups : []).flatMap((group) =>
    (group?.schedules || []).map((schedule) => ({
      ...schedule,
      dateKey: group.date || String(schedule.date || "").slice(0, 10),
    }))
  );

export const getVisitStatus = (status) => {
  if (status === "completed") return "completed";
  if (status === "cancelled") return "cancelled";
  return "scheduled";
};

export const VISIT_STATUS_LABELS = {
  completed: "Completed",
  scheduled: "Scheduled",
  cancelled: "Cancelled",
};

const toDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// "09:30" - 12-hour clock without the suffix, as on the visit cards.
export const formatClockShort = (value) => {
  const date = toDate(value);
  if (!date) return "--:--";
  const hours = date.getHours() % 12 || 12;
  return `${pad(hours)}:${pad(date.getMinutes())}`;
};

// "09:30 AM" - same format the backend stores in `timeSlot`.
export const formatClock12 = (value) => {
  const date = toDate(value);
  if (!date) return "";
  return `${formatClockShort(date)} ${date.getHours() >= 12 ? "PM" : "AM"}`;
};

export const formatTimeValue12 = (hhmm) => {
  const [hours, minutes] = String(hhmm).split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return "";
  return formatClock12(new Date(2000, 0, 1, hours, minutes));
};

export const buildTimeSlot = (startTime, endTime) =>
  `${formatClock12(startTime)} - ${formatClock12(endTime)}`;

// "HH:mm" (24h) of a stored timestamp, in local time.
export const toTimeValue = (value) => {
  const date = toDate(value);
  return date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : "";
};

export const combineDateAndTime = (dateKey, hhmm) => {
  const [hours, minutes] = String(hhmm).split(":").map(Number);
  const date = parseDateKey(dateKey);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// 15-minute slots from 5:00 AM to 10:00 PM, as "HH:mm".
export const TIME_OPTIONS = Array.from({ length: (22 - 5) * 4 + 1 }, (_, index) => {
  const minutes = 5 * 60 + index * 15;
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
});

const getRoles = (user) => [user?.role, ...(Array.isArray(user?.roles) ? user.roles : [])].filter(Boolean);

export const isCoachUser = (user) => getRoles(user).some((role) => COACH_ROLES.includes(role));

export const isStudentUser = (user) => getRoles(user).includes("student");

export const countStudentsByCentre = (users) => {
  const counts = {};
  (users || []).forEach((user) => {
    if (!isStudentUser(user) || user.status === "inactive") return;
    new Set((user.balagruhaIds || []).map(getId).filter(Boolean)).forEach((centreId) => {
      counts[centreId] = (counts[centreId] || 0) + 1;
    });
  });
  return counts;
};

export const sortByStartTime = (visits) =>
  [...visits].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

// Pulls a readable message (plus the clashing slot, when the API reports one)
// out of a failed create/update response.
export const describeScheduleError = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  const overlapList = data?.overlappingSchedules;
  const overlap = Array.isArray(overlapList)
    ? overlapList[0]?.overlappingSchedule
    : overlapList;
  if (overlap?.startTime && overlap?.endTime) {
    return `${message}. The coach already has a visit from ${formatClock12(overlap.startTime)} to ${formatClock12(overlap.endTime)} on that date.`;
  }
  return message;
};
